import { AlertTriangle, Check, Clock, Copy, Download, Filter, LineChart, RefreshCw, Search, SlidersHorizontal } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import PerformanceChartModal from "../../components/PerformanceChartModal";
import ResponsiveActionButton from "../../components/ResponsiveActionButton";
import UserConfigurationModal from "../../components/UserConfigurationModal";
import AdminUserStrategyModal, {
  AdminUserStrategyModalSummary,
} from "../../components/admin/AdminUserStrategyModal";
import { useTheme } from "../../contexts/ThemeContext";
import investorStrategyService, {
  BulkUserStrategiesResult,
  BulkUserStrategy,
  getSignedDriftedWeight,
  getSignedWeight,
} from "../../services/investorStrategyService";
import { productSearchService } from "../../services/productSearchService";
import { usersService, User } from "../../services/usersService";
import type { ProductSearchItem } from "../../types/productV3";
import { buildCSV, triggerCSVDownload } from "../../utils/csvExport";
import {
  buildProductLookup,
  ProductLookupEntry,
  ProductLookupKey,
} from "../../utils/strategyUtils";

export interface UserContactInfo {
  name: string | null;
  email: string;
}
type UserContactLookup = Map<string, UserContactInfo>;

type SortKey = "userId" | "strategyCount" | "name" | "email";
type SortDir = "asc" | "desc";

const EMPTY_RESULT: BulkUserStrategiesResult = { totalUsers: 0, users: [] };
const EMPTY_PRODUCTS: ProductSearchItem[] = [];
const EMPTY_USER_LOOKUP: UserContactLookup = new Map();
// Max the product-search routes accept (they clamp, never reject), so the full
// catalogue walk costs the fewest possible round trips.
const PRODUCT_PAGE_SIZE = 2000;
const USER_PAGE_SIZE = 100;

const CSV_FILENAME_PREFIX = "bulk-strategies";

function abbreviateUserId(userId: string): string {
  if (userId.length <= 13) return userId;
  return `${userId.slice(0, 8)}…${userId.slice(-4)}`;
}

// Pages through /v2/users (role=INVESTOR) to build a userId -> {name, email} lookup for the
// bulk view. The investor strategies endpoint doesn't carry user contact info, and there is
// no batch-by-id endpoint, so we mirror the product-catalogue pagination pattern.
async function fetchAllInvestorUsers(signal: AbortSignal): Promise<User[]> {
  const first = await usersService.findUsers({
    roles: ["INVESTOR"],
    pageNumber: 1,
    pageSize: USER_PAGE_SIZE,
  });
  if (signal.aborted) return [];
  if (first.totalPages <= 1) return first.items;
  const rest = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, i) =>
      usersService.findUsers({
        roles: ["INVESTOR"],
        pageNumber: i + 2,
        pageSize: USER_PAGE_SIZE,
      })
    )
  );
  if (signal.aborted) return [];
  const items = [...first.items];
  for (const page of rest) items.push(...page.items);
  return items;
}

function buildUserLookup(users: User[]): UserContactLookup {
  const map: UserContactLookup = new Map();
  for (const u of users) {
    map.set(u.id, { name: u.name ?? null, email: u.email });
  }
  return map;
}

// Pages through the admin product search endpoint to materialize every BASKET/PORTFOLIO the
// server knows about. Admin is required here because a bulk-strategies viewer is always an
// admin, and non-admin search hides private products.
async function fetchAllAdminProducts(signal: AbortSignal): Promise<ProductSearchItem[]> {
  const first = await productSearchService.searchAdminProducts(
    { page: 1, pageSize: PRODUCT_PAGE_SIZE },
    signal
  );
  if (first.totalPages <= 1) return first.items;
  const rest = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, i) =>
      productSearchService.searchAdminProducts(
        { page: i + 2, pageSize: PRODUCT_PAGE_SIZE },
        signal
      )
    )
  );
  const items = [...first.items];
  for (const page of rest) items.push(...page.items);
  return items;
}

// Values observed in production UserConfiguration.data for these boolean flags are stored as
// strings ("true" / "false"). Treat anything else (including missing keys) as false.
function readBooleanFlag(data: Record<string, unknown> | null | undefined, key: string): boolean {
  if (!data) return false;
  const raw = data[key];
  if (typeof raw === "string") return raw.trim().toLowerCase() === "true";
  if (typeof raw === "boolean") return raw;
  return false;
}

function matchesSearch(
  user: BulkUserStrategy,
  contact: UserContactInfo | undefined,
  term: string
): boolean {
  if (!term) return true;
  const needle = term.toLowerCase();
  if (user.userId.toLowerCase().includes(needle)) return true;
  const ibkr = user.userConfig?.ibkrAccountId;
  if (ibkr && ibkr.toLowerCase().includes(needle)) return true;
  if (contact?.email && contact.email.toLowerCase().includes(needle)) return true;
  if (contact?.name && contact.name.toLowerCase().includes(needle)) return true;
  return false;
}

function compareUsers(
  a: BulkUserStrategy,
  b: BulkUserStrategy,
  key: SortKey,
  dir: SortDir,
  userLookup: UserContactLookup
): number {
  const mult = dir === "asc" ? 1 : -1;
  if (key === "userId") {
    return a.userId.localeCompare(b.userId) * mult;
  }
  if (key === "strategyCount") {
    return (a.strategies.length - b.strategies.length) * mult;
  }
  const aContact = userLookup.get(a.userId);
  const bContact = userLookup.get(b.userId);
  if (key === "email") {
    const ae = aContact?.email ?? "";
    const be = bContact?.email ?? "";
    return ae.localeCompare(be) * mult;
  }
  // name
  const an = aContact?.name ?? "";
  const bn = bContact?.name ?? "";
  return an.localeCompare(bn) * mult;
}

function buildCsvRows(
  users: BulkUserStrategy[],
  productLookup: Map<ProductLookupKey, ProductLookupEntry>,
  userLookup: UserContactLookup
): string[][] {
  const rows: string[][] = [];
  for (const user of users) {
    const contact = userLookup.get(user.userId);
    const userName = contact?.name ?? "";
    const userEmail = contact?.email ?? "";
    const ibkrAccountId = user.userConfig?.ibkrAccountId ?? "";
    const tradingEnabled = readBooleanFlag(user.userConfig?.data, "tradingenabled") ? "true" : "false";
    const isCustomer = readBooleanFlag(user.userConfig?.data, "iscustomer") ? "true" : "false";
    const isStale = user.isStrategyStale ? "true" : "false";
    const isInconsistent = user.dataInconsistent ? "true" : "false";

    if (user.strategies.length === 0) {
      rows.push([
        user.userId,
        userName,
        userEmail,
        ibkrAccountId,
        tradingEnabled,
        isCustomer,
        "",
        "",
        "",
        "",
        "",
        "",
        isStale,
        isInconsistent,
      ]);
      continue;
    }

    for (const strategy of user.strategies) {
      const signedWeight = getSignedWeight(strategy);
      const signedDrift = getSignedDriftedWeight(strategy);
      const lookupEntry = productLookup.get(
        `${strategy.entityType}-${strategy.productId}` as ProductLookupKey
      );
      const productTitle = lookupEntry?.title ?? "";
      const providerName = lookupEntry?.providerName ?? "";
      rows.push([
        user.userId,
        userName,
        userEmail,
        ibkrAccountId,
        tradingEnabled,
        isCustomer,
        strategy.entityType,
        String(strategy.productId),
        productTitle,
        providerName,
        signedWeight.toString(),
        signedDrift === null ? "" : signedDrift.toString(),
        isStale,
        isInconsistent,
      ]);
    }
  }
  return rows;
}

const StatusBadge: React.FC<{
  user: BulkUserStrategy;
}> = ({ user }) => {
  if (user.dataInconsistent) {
    return (
      <span
        title="Strategy data inconsistency: snapshot missing"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
      >
        <AlertTriangle className="w-3 h-3" />
        Inconsistent
      </span>
    );
  }
  if (user.isStrategyStale) {
    return (
      <span
        title="Latest factor snapshot is stale; drifted weights unavailable"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
      >
        <Clock className="w-3 h-3" />
        Drift unavailable
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
      OK
    </span>
  );
};

const BooleanPill: React.FC<{ value: boolean; trueLabel: string; falseLabel?: string }> = ({ value, trueLabel, falseLabel }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
      value
        ? "bg-rp-amber/10 text-rp-amber border border-rp-amber/30"
        : "bg-gray-100 dark:bg-rp-surface-raised text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
    }`}
  >
    {value ? trueLabel : falseLabel ?? "—"}
  </span>
);

const CopyUserIdButton: React.FC<{ userId: string; copied: boolean; onCopy: (userId: string) => void }> = ({
  userId,
  copied,
  onCopy,
}) => (
  <button
    type="button"
    onClick={e => {
      e.stopPropagation();
      onCopy(userId);
    }}
    aria-label={`Copy user ID ${userId}`}
    title={copied ? "Copied!" : `Copy ${userId}`}
    className="inline-flex items-center justify-center w-5 h-5 rounded text-gray-400 dark:text-gray-500 hover:text-rp-amber hover:bg-gray-100 dark:hover:bg-rp-surface-raised transition-colors focus:outline-none focus:ring-1 focus:ring-rp-amber"
  >
    {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
  </button>
);

const UserRow: React.FC<{
  user: BulkUserStrategy;
  contact: UserContactInfo | undefined;
  copiedUserId: string | null;
  onCopyUserId: (userId: string) => void;
  onOpen: () => void;
  onOpenChart: () => void;
  onEditConfig: () => void;
}> = ({ user, contact, copiedUserId, onCopyUserId, onOpen, onOpenChart, onEditConfig }) => {
  const tradingEnabled = readBooleanFlag(user.userConfig?.data, "tradingenabled");
  const isCustomer = readBooleanFlag(user.userConfig?.data, "iscustomer");
  const displayName = contact?.name?.trim() ? contact.name : null;
  const displayEmail = contact?.email ?? null;

  return (
    <tr
      onClick={onOpen}
      className={`border-b border-gray-200 dark:border-rp-separator cursor-pointer transition-colors ${
        user.dataInconsistent
          ? "bg-red-50/40 dark:bg-red-900/10 hover:bg-red-50/70 dark:hover:bg-red-900/15"
          : "hover:bg-gray-50 dark:hover:bg-rp-surface"
      }`}
    >
      <td className="px-3 py-3 text-xs text-gray-900 dark:text-white">
        {displayName ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
      </td>
      <td className="px-3 py-3 text-xs text-gray-700 dark:text-gray-300 truncate max-w-[20rem]" title={displayEmail ?? undefined}>
        {displayEmail ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
      </td>
      <td className="px-3 py-3 text-xs text-gray-700 dark:text-gray-300">
        <div className="inline-flex items-center gap-1.5">
          <span className="font-mono" title={user.userId}>
            {abbreviateUserId(user.userId)}
          </span>
          <CopyUserIdButton
            userId={user.userId}
            copied={copiedUserId === user.userId}
            onCopy={onCopyUserId}
          />
        </div>
      </td>
      <td className="px-3 py-3 text-xs font-mono text-gray-700 dark:text-gray-300">
        {user.userConfig?.ibkrAccountId ?? (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        <BooleanPill value={tradingEnabled} trueLabel="Trading" falseLabel="Off" />
      </td>
      <td className="px-3 py-3">
        <BooleanPill value={isCustomer} trueLabel="Customer" falseLabel="—" />
      </td>
      <td className="px-3 py-3 text-xs text-gray-700 dark:text-gray-300 text-right font-mono">
        {user.strategies.length}
      </td>
      <td className="px-3 py-3">
        <StatusBadge user={user} />
      </td>
      <td className="px-3 py-3 text-right">
        <div className="inline-flex items-center gap-3 justify-end">
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onEditConfig();
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-rp-amber hover:underline focus:outline-none focus:underline"
            title="Edit customer / trading flags"
            aria-label="Edit user configuration flags"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Edit flags
          </button>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onOpenChart();
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-rp-amber hover:underline focus:outline-none focus:underline"
            title="Open performance chart"
          >
            <LineChart className="w-3.5 h-3.5" />
            Chart
          </button>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onOpen();
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-rp-amber hover:underline focus:outline-none focus:underline"
            title="Open strategy modal"
          >
            View
          </button>
        </div>
      </td>
    </tr>
  );
};

const AdminBulkStrategiesPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [result, setResult] = useState<BulkUserStrategiesResult>(EMPTY_RESULT);
  // Raw catalogue, not just the derived lookup: the strategy modal reuses these
  // items instead of paging /v3/products/search/admin a second time.
  const [productCatalogue, setProductCatalogue] = useState<ProductSearchItem[]>(EMPTY_PRODUCTS);
  const [userLookup, setUserLookup] = useState<UserContactLookup>(EMPTY_USER_LOOKUP);
  const [loading, setLoading] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [filterTrading, setFilterTrading] = useState(false);
  const [filterCustomer, setFilterCustomer] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [chartUserId, setChartUserId] = useState<string | null>(null);
  const [configUserId, setConfigUserId] = useState<string | null>(null);
  const [copiedUserId, setCopiedUserId] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const productsAbortRef = useRef<AbortController | null>(null);
  const usersAbortRef = useRef<AbortController | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStrategies = useCallback(async (requestId: number) => {
    try {
      const data = await investorStrategyService.getAllStrategies();
      if (requestIdRef.current !== requestId) return;
      setResult(data);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load bulk investor strategies.";
      setError(message);
    }
  }, []);

  const fetchProducts = useCallback(async (requestId: number) => {
    // Cancel any in-flight product fetch before starting a new one.
    productsAbortRef.current?.abort();
    const controller = new AbortController();
    productsAbortRef.current = controller;
    setIsLoadingProducts(true);
    setProductsError(null);
    try {
      const items = await fetchAllAdminProducts(controller.signal);
      if (requestIdRef.current !== requestId) return;
      setProductCatalogue(items);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (requestIdRef.current !== requestId) return;
      setProductsError(
        err instanceof Error
          ? err.message
          : "Failed to load product catalogue; showing IDs only."
      );
    } finally {
      if (productsAbortRef.current === controller) {
        productsAbortRef.current = null;
      }
      if (requestIdRef.current === requestId) setIsLoadingProducts(false);
    }
  }, []);

  const fetchUsers = useCallback(async (requestId: number) => {
    usersAbortRef.current?.abort();
    const controller = new AbortController();
    usersAbortRef.current = controller;
    setIsLoadingUsers(true);
    setUsersError(null);
    try {
      const users = await fetchAllInvestorUsers(controller.signal);
      if (controller.signal.aborted) return;
      if (requestIdRef.current !== requestId) return;
      setUserLookup(buildUserLookup(users));
    } catch (err) {
      if (controller.signal.aborted) return;
      if (requestIdRef.current !== requestId) return;
      setUsersError(
        err instanceof Error
          ? err.message
          : "Failed to load user directory; names and emails will be blank."
      );
    } finally {
      if (usersAbortRef.current === controller) {
        usersAbortRef.current = null;
      }
      if (requestIdRef.current === requestId) setIsLoadingUsers(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    const thisRequest = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      // Kick off all three fetches in parallel. Strategies drives the primary spinner;
      // products and user directory hydrate asynchronously with graceful ID-only fallbacks.
      await Promise.all([
        fetchStrategies(thisRequest),
        fetchProducts(thisRequest),
        fetchUsers(thisRequest),
      ]);
    } finally {
      if (requestIdRef.current === thisRequest) setLoading(false);
    }
  }, [fetchStrategies, fetchProducts, fetchUsers]);

  useEffect(() => {
    fetchAll();
    return () => {
      productsAbortRef.current?.abort();
      usersAbortRef.current?.abort();
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, [fetchAll]);

  const handleCopyUserId = useCallback((userId: string) => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    navigator.clipboard.writeText(userId).catch(() => {
      // Best-effort: some browsers/contexts may block clipboard writes; surface silently.
    });
    setCopiedUserId(userId);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopiedUserId(null), 1500);
  }, []);

  const productLookup = useMemo(
    () => buildProductLookup(productCatalogue),
    [productCatalogue]
  );

  // Shared with AdminUserStrategyModal so it does not re-page the same admin
  // catalogue endpoint on every open. Memoized: an unstable object here would
  // churn every product-derived memo inside useStrategyData.
  const injectedProducts = useMemo(
    () => ({
      items: productCatalogue,
      isLoading: isLoadingProducts,
      error: productsError,
    }),
    [productCatalogue, isLoadingProducts, productsError]
  );

  // Defense in depth: even if a future response drift slips past the service normalizer,
  // treat a missing users array as an empty list rather than crashing the admin page.
  const users: BulkUserStrategy[] = useMemo(
    () => (Array.isArray(result.users) ? result.users : []),
    [result.users]
  );

  const visibleUsers = useMemo(() => {
    const filtered = users.filter(user => {
      if (!matchesSearch(user, userLookup.get(user.userId), searchTerm)) return false;
      if (onlyIssues && !user.dataInconsistent && !user.isStrategyStale) return false;
      if (filterTrading && !readBooleanFlag(user.userConfig?.data, "tradingenabled")) return false;
      if (filterCustomer && !readBooleanFlag(user.userConfig?.data, "iscustomer")) return false;
      return true;
    });
    return [...filtered].sort((a, b) => compareUsers(a, b, sortKey, sortDir, userLookup));
  }, [users, searchTerm, onlyIssues, filterTrading, filterCustomer, sortKey, sortDir, userLookup]);

  const summary = useMemo(() => {
    let stale = 0;
    let inconsistent = 0;
    for (const user of users) {
      if (user.dataInconsistent) inconsistent++;
      else if (user.isStrategyStale) stale++;
    }
    return { stale, inconsistent };
  }, [users]);

  const selectedUser = useMemo(
    () => (selectedUserId ? users.find(u => u.userId === selectedUserId) ?? null : null),
    [selectedUserId, users]
  );

  const configUserName = useMemo(() => {
    if (!configUserId) return null;
    const contact = userLookup.get(configUserId);
    return contact?.name?.trim() || contact?.email || null;
  }, [configUserId, userLookup]);

  const handleConfigSaved = useCallback(() => {
    // Re-fetch strategies so the row's Trading/Customer pills and Inconsistent badge reflect
    // the freshly persisted userConfig. Reuses the current requestId instead of bumping so
    // that any in-flight product or user-directory fetches from the initial load still
    // commit their results; if a full fetchAll starts later it will bump the id and our
    // stale strategies write will no-op as expected.
    fetchStrategies(requestIdRef.current);
  }, [fetchStrategies]);

  const selectedUserSummary: AdminUserStrategyModalSummary | null = useMemo(() => {
    if (!selectedUser) return null;
    const contact = userLookup.get(selectedUser.userId);
    return {
      name: contact?.name ?? null,
      email: contact?.email ?? null,
      ibkrAccountId: selectedUser.userConfig?.ibkrAccountId ?? null,
      tradingEnabled: readBooleanFlag(selectedUser.userConfig?.data, "tradingenabled"),
      isCustomer: readBooleanFlag(selectedUser.userConfig?.data, "iscustomer"),
      isStrategyStale: Boolean(selectedUser.isStrategyStale),
      dataInconsistent: Boolean(selectedUser.dataInconsistent),
    };
  }, [selectedUser, userLookup]);

  const toggleSort = useCallback((key: SortKey) => {
    setSortKey(prevKey => {
      if (prevKey === key) {
        setSortDir(prevDir => (prevDir === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      setSortDir("asc");
      return key;
    });
  }, []);

  const handleExportCsv = useCallback(() => {
    if (visibleUsers.length === 0) return;
    const headers = [
      "userId",
      "userName",
      "userEmail",
      "ibkrAccountId",
      "tradingEnabled",
      "isCustomer",
      "entityType",
      "productId",
      "productTitle",
      "providerName",
      "signedWeight",
      "signedDriftedWeight",
      "isStrategyStale",
      "dataInconsistent",
    ];
    const rows = buildCsvRows(visibleUsers, productLookup, userLookup);
    const csv = buildCSV(headers, rows);
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    triggerCSVDownload(csv, `${CSV_FILENAME_PREFIX}-${stamp}.csv`);
  }, [visibleUsers, productLookup, userLookup]);

  const sortIndicator = (key: SortKey): string => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? "▲" : "▼";
  };

  return (
    <AdminPageLayout
      title="Bulk Strategies"
      description="All users with at least one active investor strategy, with configuration, drifted weights, and staleness flags."
      actions={
        <div className="flex items-center gap-2">
          <ResponsiveActionButton
            label="Export CSV"
            icon={<Download className="w-4 h-4" />}
            onClick={handleExportCsv}
            disabled={loading || visibleUsers.length === 0}
            variant="secondary"
          />
          <ResponsiveActionButton
            label="Refresh"
            icon={<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
            onClick={fetchAll}
            disabled={loading}
            variant="default"
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Total users
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white font-mono">
              {result.totalUsers}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Drift unavailable
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white font-mono">
              {summary.stale}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Data inconsistent
            </div>
            <div
              className={`mt-1 text-2xl font-bold font-mono ${
                summary.inconsistent > 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-900 dark:text-white"
              }`}
            >
              {summary.inconsistent}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="relative flex-1 min-w-0 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, user ID, or IBKR account"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all"
              />
            </div>
            <button
              type="button"
              onClick={() => setOnlyIssues(v => !v)}
              className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg border transition-colors ${
                onlyIssues
                  ? "bg-rp-amber text-white border-rp-amber"
                  : "bg-white dark:bg-rp-surface text-gray-600 dark:text-gray-300 border-gray-200 dark:border-rp-separator hover:border-rp-amber"
              }`}
              title="Filter to only users with stale or inconsistent data"
            >
              <Filter className="w-3.5 h-3.5" />
              Only issues
            </button>
            <button
              type="button"
              onClick={() => setFilterTrading(v => !v)}
              className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg border transition-colors ${
                filterTrading
                  ? "bg-rp-amber text-white border-rp-amber"
                  : "bg-white dark:bg-rp-surface text-gray-600 dark:text-gray-300 border-gray-200 dark:border-rp-separator hover:border-rp-amber"
              }`}
              title="Filter to only users with trading enabled"
            >
              <Filter className="w-3.5 h-3.5" />
              Trading
            </button>
            <button
              type="button"
              onClick={() => setFilterCustomer(v => !v)}
              className={`inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg border transition-colors ${
                filterCustomer
                  ? "bg-rp-amber text-white border-rp-amber"
                  : "bg-white dark:bg-rp-surface text-gray-600 dark:text-gray-300 border-gray-200 dark:border-rp-separator hover:border-rp-amber"
              }`}
              title="Filter to only customers"
            >
              <Filter className="w-3.5 h-3.5" />
              Customer
            </button>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            Click a row to view strategy, performance, and positions.
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {(isLoadingProducts || isLoadingUsers) && users.length > 0 && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            {isLoadingProducts && isLoadingUsers
              ? "Resolving product names and user directory…"
              : isLoadingProducts
              ? "Resolving product names…"
              : "Resolving user directory…"}
          </div>
        )}
        {productsError && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
            {productsError}
          </div>
        )}
        {usersError && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
            {usersError}
          </div>
        )}

        {loading && users.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div
              className={`animate-spin rounded-full h-10 w-10 border-b-2 ${
                isDarkMode ? "border-rp-amber" : "border-rp-amber"
              }`}
            ></div>
          </div>
        ) : visibleUsers.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            {users.length === 0
              ? "No users with active investor strategies."
              : "No users match the current filters."}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-rp-surface-raised border-b border-gray-200 dark:border-rp-separator">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="inline-flex items-center gap-1 hover:text-rp-amber"
                      >
                        Name <span aria-hidden="true">{sortIndicator("name")}</span>
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <button
                        type="button"
                        onClick={() => toggleSort("email")}
                        className="inline-flex items-center gap-1 hover:text-rp-amber"
                      >
                        Email <span aria-hidden="true">{sortIndicator("email")}</span>
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <button
                        type="button"
                        onClick={() => toggleSort("userId")}
                        className="inline-flex items-center gap-1 hover:text-rp-amber"
                      >
                        User ID <span aria-hidden="true">{sortIndicator("userId")}</span>
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      IBKR Account
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Trading
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Customer
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <button
                        type="button"
                        onClick={() => toggleSort("strategyCount")}
                        className="inline-flex items-center gap-1 hover:text-rp-amber"
                      >
                        Products <span aria-hidden="true">{sortIndicator("strategyCount")}</span>
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map(user => (
                    <UserRow
                      key={user.userId}
                      user={user}
                      contact={userLookup.get(user.userId)}
                      copiedUserId={copiedUserId}
                      onCopyUserId={handleCopyUserId}
                      onOpen={() => setSelectedUserId(user.userId)}
                      onOpenChart={() => setChartUserId(user.userId)}
                      onEditConfig={() => setConfigUserId(user.userId)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-gray-200 dark:border-rp-separator px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
              Showing {visibleUsers.length} of {result.totalUsers} users
            </div>
          </div>
        )}
      </div>
      <AdminUserStrategyModal
        isOpen={selectedUserId !== null}
        userId={selectedUserId}
        summary={selectedUserSummary}
        products={injectedProducts}
        onClose={() => setSelectedUserId(null)}
      />
      {chartUserId && (
        <PerformanceChartModal
          isOpen
          onClose={() => setChartUserId(null)}
          entityId={0}
          entityType="STRATEGY"
          entityName={`Strategy · ${chartUserId}`}
          dataSource="v3-factors"
          adminUserId={chartUserId}
        />
      )}
      <UserConfigurationModal
        isOpen={configUserId !== null}
        userId={configUserId}
        userName={configUserName}
        onClose={() => setConfigUserId(null)}
        onSave={handleConfigSaved}
      />
    </AdminPageLayout>
  );
};

export default AdminBulkStrategiesPage;
